/**
 * Log-freshness rule, shared by the traffic-agg Lambda (which logs an ERROR when
 * it writes a summary from stale logs) and the admin overview (which warns above
 * the traffic tiles). ONE implementation so the two cannot disagree on when the
 * numbers stop meaning "zero traffic" and start meaning "no data".
 *
 * Why it exists: CloudFront logs once stopped arriving and the daily summary kept
 * being rewritten with honest zeros for 11 weeks. Nothing about a zero summary
 * says whether anyone visited; only the age of the newest log object does.
 */

/** Logs older than this (newest object vs. now) mean the pipeline has stalled. */
export const LOG_STALE_AFTER_MS = 2 * 24 * 60 * 60 * 1000;

export interface LogFreshnessFields {
  /** .gz log objects inside the read window. */
  logObjectsInWindow: number;
  /** ISO 8601 LastModified of the newest log object, or null if there are none. */
  newestLogAt: string | null;
}

/**
 * True when the summary cannot be trusted as a traffic count:
 *   - no log object fell inside the window, OR
 *   - there are no log objects at all (newestLogAt null), OR
 *   - the newest log object is MORE than LOG_STALE_AFTER_MS older than `now`
 *     (exactly at the threshold is still fresh).
 * An unparseable newestLogAt is treated as stale: a date nobody can read cannot
 * vouch for the numbers.
 */
export function isLogStale(fields: LogFreshnessFields, now: Date): boolean {
  if (fields.logObjectsInWindow === 0) return true;
  if (fields.newestLogAt === null) return true;
  const newest = Date.parse(fields.newestLogAt);
  if (Number.isNaN(newest)) return true;
  return now.getTime() - newest > LOG_STALE_AFTER_MS;
}

/**
 * Narrow an untrusted object (e.g. a parsed stats/traffic.json) to its
 * freshness fields, or null when they are absent or malformed. Null means
 * "freshness unknown" -- an older summary that predates the fields -- and the
 * caller must neither warn nor reassure on it.
 */
export function readLogFreshness(value: {
  logObjectsInWindow?: unknown;
  newestLogAt?: unknown;
}): LogFreshnessFields | null {
  const count = value.logObjectsInWindow;
  const newest = value.newestLogAt;
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) return null;
  if (newest === null) return { logObjectsInWindow: count, newestLogAt: null };
  if (typeof newest !== 'string' || Number.isNaN(Date.parse(newest))) return null;
  return { logObjectsInWindow: count, newestLogAt: newest };
}
